// Every English string for recording stock that was already bought and paid
// for (F-CTRL-N75): "Already bought" in Purchasing, and the items of a Day
// Close stock cash-out. There is no "market buy" any more — it is a purchase.
import type { MarketDeliverTo, MarketPaidBy } from './marketTypes'

export const BT = {
  // Purchasing → New purchase
  newPurchase: 'New purchase',
  chooseTitle: 'New purchase',
  chooseHelp: 'Is this an order you are placing, or something already bought?',
  orderTitle: 'Order from a supplier',
  orderHelp: 'Send an order now and receive the delivery when it arrives.',
  boughtTitle: 'Already bought & paid',
  boughtHelp: 'Someone went to the market or a shop and paid. It goes straight into stock.',

  // The already-bought form
  title: 'Already bought & paid',
  help: 'What was bought, what was paid, and a photo of the bill. It goes straight into stock where it was delivered.',
  deliverTo: 'Delivered to',
  places: { store: 'Store', kitchen: 'Kitchen' } satisfies Record<MarketDeliverTo, string>,
  paidBy: 'Who paid?',
  payers: {
    till: 'Cash from the till',
    petty_cash: 'Petty cash tin',
    buyer: 'The buyer (to pay back)',
    company: 'Company account',
  } satisfies Record<MarketPaidBy, string>,
  payerHelp: {
    till: 'Taken out of today’s till. It shows at Day Close as money taken out.',
    petty_cash: 'From the separate petty-cash tin. The till is not touched.',
    buyer: 'Paid out of their own pocket. Pay them back later.',
    company: 'Paid by bank or card, or owed to the vendor.',
  } satisfies Record<MarketPaidBy, string>,
  buyer: 'Buyer’s name',
  vendor: 'Vendor',
  vendorStall: 'A market stall or shop (type its name)',
  supplier: 'Shop / stall (optional)',
  supplierPlaceholder: 'Akbari mandi, stall 4…',
  search: 'Search items or scan…',
  noMatch: 'No items match.',
  price: 'Price per unit',
  lot: 'Lot (optional)',
  expiry: 'Use by (optional)',
  expiryRequired: 'Use by',
  remove: 'Remove',
  total: 'Total',
  photos: 'Bill photos',
  addPhoto: 'Add a photo of the bill',
  photoBusy: 'Preparing photo…',
  notes: 'Notes (optional)',
  save: 'Record the purchase',
  saving: 'Saving…',
  cancel: 'Cancel',
  saved: (total: string) => `Purchase recorded — ${total}`,
  toReimburse: (who: string) => `pay back ${who}`,
  problems: {
    noLines: 'Add what was bought.',
    badLine: 'Every line needs a quantity and the price paid.',
    needExpiry: 'Items marked * go off: enter their use-by date.',
    needBuyer: 'Enter who paid.',
  },

  // Day Close — a stock cash-out
  whatBought: 'What did you buy?',
  whatBoughtHelp: 'Add the items so they go into stock. The amount becomes their total.',
  addLater: 'Add the items later',
  addLaterHelp: 'Saved as “stock not added” until someone adds the items from the bill.',
  addNow: 'Add the items now',
  amountFromItems: 'The amount is the items’ total.',
  stockNotAdded: 'Stock not added',
  stockAdded: 'Stock added',
  addItems: 'Add items',
  addItemsTitle: 'Add what this cash bought',
  addItemsHelp: (amount: string) => `${amount} was taken out for stock. Add the items so they go into stock. No money is recorded again.`,
  cashTaken: 'Cash taken out',
  itemsTotal: 'Items total',
  differs: 'The items and the cash don’t match. Check the prices, or say why they differ.',
  differenceNote: 'Why do they differ?',
  differencePlaceholder: 'e.g. a bag of onions not on the bill',
  saveItems: 'Add to stock',
  itemsSaved: 'Items added to stock',
  lockedHint: 'This cash paid for stock that is on the shelf, so the amount can’t change.',
  needingItems: (n: number) => `${n} stock cash-out${n === 1 ? '' : 's'} at Day Close ${n === 1 ? 'has' : 'have'} no items yet`,
  needingItemsHelp: 'The cash is recorded but the stock isn’t. Add the items from the bills.',
  needingItemsAction: 'Open Day Close',
  closeNote: (n: number) => `${n} stock cash-out${n === 1 ? '' : 's'} today ${n === 1 ? 'has' : 'have'} no items. You can close anyway and add them later.`,
} as const
